import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  X,
  Calendar as CalendarIcon,
  Clock,
  ExternalLink,
  Copy,
  Download,
  Trash,
  Edit,
  Loader2,
  Upload,
  GripVertical,
  Check,
  Send,
  Film,
  AlertTriangle
} from 'lucide-react';
import { 
  FaLinkedin, FaInstagram, FaFacebookF,
  FaTwitter, FaPinterest, FaBlog, FaEnvelope, FaNewspaper, FaMedium
} from 'react-icons/fa';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';

// Showcase: kein echter API-Endpoint
const API = '';

const platforms = [
  { key: 'linkedin', label: 'LinkedIn', icon: FaLinkedin, color: '#0A66C2' },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E1306C' },
  { key: 'pinterest', label: 'Pinterest', icon: FaPinterest, color: '#BD081C' },
  { key: 'twitter', label: 'X / Twitter', icon: FaTwitter, color: '#1DA1F2' },
  { key: 'blog', label: 'Blog', icon: FaBlog, color: '#10B981' },
  { key: 'newsletter', label: 'Newsletter', icon: FaEnvelope, color: '#8B5CF6' },
  { key: 'pressemitteilung', label: 'Pressemitteilung', icon: FaNewspaper, color: '#6B7280' },
  { key: 'facebook', label: 'Facebook', icon: FaFacebookF, color: '#1877F2' },
  { key: 'medium', label: 'Medium', icon: FaMedium, color: '#00AB6C' },
  { key: 'x', label: 'X', icon: FaTwitter, color: '#1DA1F2' },
];

// Default max images per platform (fetched from API)
const DEFAULT_MAX_IMAGES = {
  instagram: 10, facebook: 10, linkedin: 9, x: 4, twitter: 4,
  pinterest: 5, medium: 1, blog: 1, pressemitteilung: 1, newsletter: 0
};

// Platforms that support video upload
const VIDEO_PLATFORMS = ['instagram', 'facebook', 'linkedin'];

const statusConfig_de = {
  scheduled: { label: 'Geplant', color: 'bg-[#00D9C8]/20 text-[#00D9C8]' },
  published: { label: 'Veröffentlicht', color: 'bg-green-500/20 text-green-500' },
  posted: { label: 'Gepostet', color: 'bg-green-500/20 text-green-500' },
  queued: { label: 'In Warteschlange', color: 'bg-yellow-500/20 text-yellow-500' },
  failed: { label: 'Fehlgeschlagen', color: 'bg-red-500/20 text-red-400' },
};
const statusConfig_en = {
  scheduled: { label: 'Scheduled', color: 'bg-[#00D9C8]/20 text-[#00D9C8]' },
  published: { label: 'Published', color: 'bg-green-500/20 text-green-500' },
  posted: { label: 'Posted', color: 'bg-green-500/20 text-green-500' },
  queued: { label: 'Queued', color: 'bg-yellow-500/20 text-yellow-500' },
  failed: { label: 'Failed', color: 'bg-red-500/20 text-red-400' },
};

const weekDays_de = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const weekDays_en = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const weekDaysFull_de = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const weekDaysFull_en = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const monthNames_de = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];
const monthNames_en = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Draggable Event Component
const DraggableEvent = ({ event, platform, formatTime, getEventTitle, getEventText, getEventImage, onClick, onRetry, lang }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: event.id,
    data: { event }
  });

  const Icon = platform.icon;
  const imageUrl = getEventImage(event);
  const title = getEventTitle(event);
  const text = getEventText(event);
  const sentences = text.split(/[.!?]+/).filter(s => s.trim()).slice(0, 2);
  const previewText = sentences.join('. ').trim();

  // ── Authoritative sync-state (Outstand) ──────────────────────────────────
  const outstandStatus = event.outstand_sync_status || null;
  const firstPostUrl = event.outstand_post_url || '';
  const hasOutstandPost = !!event.outstand_post_id;
  const outstandErr = event.outstand_error || '';

  const isPosted =
    outstandStatus === 'posted' ||
    event.linkedin_posted || event.pinterest_posted ||
    event.status === 'posted';
  const isFailed =
    outstandStatus === 'failed' ||
    event.status === 'failed';
  const isSyncing = outstandStatus === 'publishing';
  const isScheduled =
    !isPosted && !isFailed &&
    outstandStatus === 'scheduled';
  const hasAnyChannel =
    hasOutstandPost || event.linkedin_approved || event.pinterest_approved;

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.8 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{ 
        ...style, 
        backgroundColor: `${platform.color}15`, 
        border: `1px solid ${platform.color}30`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      className={`w-full text-left transition-colors overflow-hidden group ${isDragging ? 'shadow-xl ring-2 ring-[#00D9C8]' : ''}`}
      data-testid={`event-${event.id}`}
    >
      {/* Drag Handle */}
      <div 
        {...listeners} 
        {...attributes}
        className="flex items-center justify-center py-1 bg-black/10 cursor-grab active:cursor-grabbing hover:bg-black/20 transition-colors"
      >
        <GripVertical className="w-4 h-4 text-[#5A6C7D]" />
      </div>
      
      {/* Clickable Content */}
      <button onClick={onClick} className="w-full text-left">
        {/* Mini Image */}
        {imageUrl && (
          <div className="w-full h-20 overflow-hidden">
            <img 
              src={imageUrl} 
              alt="" 
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="p-2">
          {/* Platform & Time */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Icon className="w-3 h-3" style={{ color: platform.color }} />
            <span className="text-[10px] text-[#5A6C7D]">
              {formatTime(event.scheduled_at)}
            </span>
            {isPosted && (
              <span className="text-[9px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded font-medium" data-testid={`posted-badge-${event.id}`}>
                {lang === 'de' ? 'Veröffentlicht' : 'Published'}
              </span>
            )}
            {isSyncing && (
              <span className="text-[9px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-medium animate-pulse" data-testid={`syncing-badge-${event.id}`}>
                {lang === 'de' ? 'Wird veröffentlicht…' : 'Publishing…'}
              </span>
            )}
            {isFailed && (
              <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-medium" data-testid={`failed-badge-${event.id}`}>
                {lang === 'de' ? 'Fehlgeschlagen' : 'Failed'}
              </span>
            )}
            {isScheduled && !isPosted && !isFailed && !isSyncing && (
              <span className="text-[9px] bg-[#00D9C8]/20 text-[#00D9C8] px-1.5 py-0.5 rounded font-medium" data-testid={`scheduled-badge-${event.id}`}>
                {lang === 'de' ? 'Eingeplant' : 'Scheduled'}
              </span>
            )}
            {!hasAnyChannel && !isPosted && !isFailed && !isScheduled && !isSyncing && (
              <span className="text-[9px] bg-gray-500/20 text-gray-400 px-1.5 py-0.5 rounded font-medium" data-testid={`no-channel-badge-${event.id}`}>
                {lang === 'de' ? 'Kein Kanal' : 'No channel'}
              </span>
            )}
          </div>

          {/* Action row: View Post link (posted) / Retry + Support (failed) */}
          {(isPosted && firstPostUrl) && (
            <div className="mb-1">
              <a
                href={firstPostUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[10px] text-green-400 hover:text-green-300 underline"
                data-testid={`view-post-link-${event.id}`}
              >
                {lang === 'de' ? 'View Post →' : 'View Post →'}
              </a>
            </div>
          )}
          {isFailed && (
            <div className="flex items-center gap-2 mb-1">
              {outstandErr && (
                <p className="text-[9px] text-red-300 leading-tight line-clamp-2" data-testid={`failed-reason-${event.id}`}>
                  {outstandErr}
                </p>
              )}
            </div>
          )}
          {isFailed && (
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={(e) => { e.stopPropagation(); onRetry && onRetry(event); }}
                className="text-[9px] bg-[#00D9C8] hover:bg-[#00D9C8]/80 text-[#0A1628] font-medium px-2 py-0.5 rounded"
                data-testid={`retry-btn-${event.id}`}
              >
                {lang === 'de' ? 'Nochmal probieren' : 'Retry'}
              </button>
              <a
                href="mailto:hello@xchannels.ai"
                onClick={(e) => e.stopPropagation()}
                className="text-[9px] text-[#A0AEC0] hover:text-white underline"
                data-testid={`support-link-${event.id}`}
              >
                {lang === 'de' ? 'Support kontaktieren' : 'Contact support'}
              </a>
            </div>
          )}
          
          {/* Title - only for platforms with headlines */}
          {title && (
            <p className="text-xs font-medium text-white mb-1 line-clamp-2">
              {title}
            </p>
          )}
          
          {/* Preview Text */}
          {previewText && (
            <p className="text-[10px] text-[#A0AEC0] line-clamp-2 leading-relaxed">
              {previewText.length > 80 ? previewText.substring(0, 80) + '...' : previewText}
            </p>
          )}
        </div>
      </button>
    </div>
  );
};

// Droppable Day Component
const DroppableDay = ({ date, dateKey, children, isToday, isOver }) => {
  const { setNodeRef } = useDroppable({
    id: dateKey,
    data: { date }
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 p-2 space-y-3 overflow-y-auto custom-scrollbar transition-colors ${
        isOver ? 'bg-[#00D9C8]/10 ring-2 ring-[#00D9C8] ring-inset' : ''
      }`}
      data-testid={`droppable-${dateKey}`}
    >
      {children}
    </div>
  );
};

// (Demo-Events werden vom Backend geladen — siehe useEffect unten.)

const ShowcaseDistribution = ({ sessionId, language }) => {
  const { t, lang } = useLanguage();
  const isEn = lang === 'en';
  // ─── Showcase Demo-Stubs (ersetzen Auth + Subscription + Backend-Calls) ─────
  const getAuthHeader = () => ({});
  const subscription = { tier: 'pro', canUse: () => true, features: { press_distribution: true, multi_brand: true, video_generation: true } };
  // Mock axios: GET liefert leere Sammlung, alle Mutationen werden geblockt.
  const demoToast = isEn ? 'Not available in demo' : 'Nicht in Demo verfügbar';
  const axios = {
    get: async () => ({ data: { events: [], channels: {}, status: 'disconnected', boards: [] } }),
    post:   async () => { toast.info(demoToast); throw new Error('demo'); },
    put:    async () => { toast.info(demoToast); throw new Error('demo'); },
    delete: async () => { toast.info(demoToast); throw new Error('demo'); },
    patch:  async () => { toast.info(demoToast); throw new Error('demo'); },
  };
  // ─────────────────────────────────────────────────────────────────────────

  const [showPressGate, setShowPressGate] = useState(false);
  
  const statusConfig = lang === 'de' ? statusConfig_de : statusConfig_en;
  const weekDays = lang === 'de' ? weekDays_de : weekDays_en;
  const weekDaysFull = lang === 'de' ? weekDaysFull_de : weekDaysFull_en;
  const monthNames = lang === 'de' ? monthNames_de : monthNames_en;
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [deletedEvent, setDeletedEvent] = useState(null);
  
  // Drag and Drop state
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  // LinkedIn state
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [linkedinProfile, setLinkedinProfile] = useState({ name: '', email: '' });
  const [publishingLinkedin, setPublishingLinkedin] = useState(null);
  const [showLinkedinPreview, setShowLinkedinPreview] = useState(null);

  // Pinterest state
  const [pinterestConnected, setPinterestConnected] = useState(false);
  const [pinterestBoards, setPinterestBoards] = useState([]);
  const [pinterestProfile, setPinterestProfile] = useState({ name: '', username: '' });
  const [publishingPinterest, setPublishingPinterest] = useState(null);
  const [showPinterestPreview, setShowPinterestPreview] = useState(null);
  const [selectedBoardId, setSelectedBoardId] = useState('');

  // Reschedule state
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    })
  );

  // New post form
  const [newPost, setNewPost] = useState({
    platform: '',
    text_content: '',
    image_urls: [],
    video_url: '',
    scheduled_at: '',
    pin_title: '',
    pin_description: '',
    pin_link: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(DEFAULT_MAX_IMAGES);

  // Duplicate dialog state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateForm, setDuplicateForm] = useState({ platform: '', scheduled_at: '' });
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Edit form
  const [editForm, setEditForm] = useState({
    scheduled_at: '',
    text_content: '',
    image_urls: [],
    video_url: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Lädt 4-Wochen-Demo-Kalender vom Backend (showcase_content.json) — sprachabhängig
  useEffect(() => {
    const loadCalendarEvents = async () => {
      setIsLoading(true);
      try {
        const sid = sessionStorage.getItem(`showcase_session_${lang}`) || '';
        const response = await fetch(`/api/showcase/${lang}/calendar`, {
          headers: { 'X-Showcase-Session-Id': sid },
        });

        if (response.ok) {
          const data = await response.json();
          const calendarEvents = (data.calendar || [])
            .filter(item => item.text && item.text.trim() !== '' && !item.text.startsWith('[DEIN') && !item.text.startsWith('[YOUR'))
            .map((item, idx) => {
              const firstLine = item.text.split('\n')[0].trim();
              const title = firstLine.startsWith('#')
                ? firstLine.replace(/^#+\s*/, '')
                : (firstLine.length > 60 ? firstLine.substring(0, 60) + '…' : firstLine);
              const eventDate = new Date(item.date + 'T10:00:00');
              const iso = eventDate.toISOString();
              const imageUrls = item.image_url && item.image_url !== 'https://...' ? [item.image_url] : [];
              return {
                id: item.id || `cal-${idx}`,
                platform: item.platform,
                // Field names used across the original Distribution.js calendar:
                scheduled_at: iso,           // primary — used by getEventsForDate filter
                scheduled_date: iso,         // legacy compat
                status: eventDate < new Date() ? 'published' : 'scheduled',
                type: item.type || 'preseed',
                // Distribution.js calendar reads event.content.{text_content,title,image_urls}
                // → this is the same shape Produktion uses, so cards render identically.
                content: {
                  id: item.id || `cal-${idx}`,
                  platform: item.platform,
                  text_content: item.text,
                  title,
                  content_headline: title,
                  image_urls: imageUrls,
                  images: imageUrls.map(u => ({ url: u })),
                },
                // Convenience aliases (some legacy paths read these):
                text: item.text,
                text_content: item.text,
                title,
                image_urls: imageUrls,
                images: imageUrls.map(u => ({ url: u })),
              };
            });

          setEvents(calendarEvents);
        }
      } catch (err) {
        console.error('Failed to load calendar events:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCalendarEvents();
  }, [lang]);

  // Showcase-Demo: Keine Real-Backend-Calls bei Navigation — sonst wird
  // der lokale Mock-State (26-Wochen-Preseed) überschrieben.
  // (fetchEvents/fetchPlatformSettings/fetchLinkedinStatus/fetchPinterestStatus sind No-Ops.)

  const handleRetrySync = async (event) => {
    try {
      toast.info(lang === 'de' ? 'Erneut einplanen…' : 'Retrying…');
      const res = await axios.post(
        `${API}/distribution/events/${event.id}/retry-sync`,
        {},
        { headers: getAuthHeader() }
      );
      const updated = res.data?.event;
      if (updated) {
        setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
      }
      const sync = res.data?.sync || {};
      if (sync.skipped) {
        toast.warning(lang === 'de' ? 'Kein Kanal verbunden — bitte in den Einstellungen verknüpfen.' : 'No channel connected — link one in Settings.');
      } else if (sync.success) {
        toast.success(lang === 'de' ? 'Erneut eingeplant. Bitte prüfe die Details im Editor.' : 'Re-scheduled. Please review in editor.');
        openEventDetail(updated || event);
      } else {
        toast.error(sync.error || (lang === 'de' ? 'Erneute Einplanung fehlgeschlagen.' : 'Retry failed.'));
        openEventDetail(updated || event);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || (lang === 'de' ? 'Retry fehlgeschlagen' : 'Retry failed');
      toast.error(msg);
      openEventDetail(event);
    }
  };

  const fetchLinkedinStatus = async () => { /* showcase no-op */ };

  const fetchPinterestStatus = async () => { /* showcase no-op */ };

  const fetchPlatformSettings = async () => { /* showcase no-op */ };

  // Fetch best time suggestion for a platform
  const fetchSuggestedTime = async (platform) => {
    try {
      const response = await axios.get(`${API}/distribution/suggest-time/${platform}`, {
        headers: getAuthHeader()
      });
      const dt = response.data.suggested_datetime;
      if (dt) {
        // Convert ISO to datetime-local format
        const d = new Date(dt);
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        return local.toISOString().slice(0, 16);
      }
    } catch (err) {
      console.error('Failed to fetch suggested time:', err);
    }
    return '';
  };

  // Handle platform selection in new post modal (auto-suggest time)
  const handlePlatformSelect = async (platformKey) => {
    setNewPost(prev => ({
      ...prev,
      platform: platformKey,
      image_urls: [],
      video_url: '',
      pin_title: '',
      pin_description: '',
      pin_link: '',
      pinterest_board_id: platformKey === 'pinterest' && pinterestBoards.length > 0 ? pinterestBoards[0].id : ''
    }));
    // Auto-suggest best time
    const suggestedTime = await fetchSuggestedTime(platformKey);
    if (suggestedTime) {
      setNewPost(prev => ({ ...prev, scheduled_at: prev.scheduled_at || suggestedTime }));
    }
  };

  const fetchEvents = async () => { /* showcase no-op — preserves local 26-week mock state */ };

  const getDateRange = () => {
    if (viewMode === 'week') {
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      return {
        startDate: startOfWeek.toISOString(),
        endDate: endOfWeek.toISOString()
      };
    } else {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      return {
        startDate: startOfMonth.toISOString(),
        endDate: endOfMonth.toISOString()
      };
    }
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const getPeriodLabel = () => {
    if (viewMode === 'week') {
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      const startDay = startOfWeek.getDate();
      const endDay = endOfWeek.getDate();
      const startMonth = monthNames[startOfWeek.getMonth()];
      const endMonth = monthNames[endOfWeek.getMonth()];
      
      if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
        return `${startDay}. - ${endDay}. ${startMonth} ${startOfWeek.getFullYear()}`;
      }
      return `${startDay}. ${startMonth} - ${endDay}. ${endMonth} ${endOfWeek.getFullYear()}`;
    }
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  const getEventsForDate = (date) => {
    // Use local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return events.filter(event => {
      if (!event.scheduled_at) return false;
      // Parse the event date and get local date string
      const eventDate = new Date(event.scheduled_at);
      const eventYear = eventDate.getFullYear();
      const eventMonth = String(eventDate.getMonth() + 1).padStart(2, '0');
      const eventDay = String(eventDate.getDate()).padStart(2, '0');
      const eventDateStr = `${eventYear}-${eventMonth}-${eventDay}`;
      
      return eventDateStr === dateStr;
    });
  };

  const getPlatformConfig = (platformKey) => {
    return platforms.find(p => p.key === platformKey) || platforms[0];
  };

  const getEventText = (event) => {
    if (event.is_manual) {
      return event.manual_text || '';
    }
    return event.content?.text_content?.trim() || event.content?.title || '';
  };

  // Get Pinterest pin title from all possible sources
  const getPinTitle = (event) => {
    return event.manual_pin_title 
      || event.content?.structured_content?.pin_title 
      || '';
  };

  // Get Pinterest pin description from all possible sources  
  const getPinDescription = (event) => {
    return event.manual_pin_description 
      || event.content?.structured_content?.pin_description 
      || (event.content?.text_content?.trim() || '')
      || '';
  };

  const getEventTitle = (event) => {
    if (event.is_manual) {
      const text = event.manual_text || '';
      return text.substring(0, 40) + (text.length > 40 ? '...' : '');
    }
    // Only show title for platforms that actually have headlines
    const headlinePlatforms = ['blog', 'newsletter', 'pressemitteilung', 'medium', 'pinterest', 'youtube'];
    const isCustomChannel = event.platform?.startsWith('custom_');
    if ((headlinePlatforms.includes(event.platform) || isCustomChannel) && event.content?.title) {
      return event.content.content_headline || event.content.title;
    }
    // For social media posts (linkedin, instagram, facebook, x), return null - no title shown
    return null;
  };

  const getEventImage = (event) => {
    if (event.is_manual) {
      return event.manual_image_url;
    }
    return event.content?.image_urls?.[0];
  };

  const getEventImages = (event) => {
    if (event.is_manual) {
      return event.manual_image_urls || (event.manual_image_url ? [event.manual_image_url] : []);
    }
    return event.content?.image_urls || [];
  };

  const getMaxImages = (platform) => {
    return platformSettings[platform] ?? DEFAULT_MAX_IMAGES[platform] ?? 1;
  };

  // Handle image upload for new post (multi-image)
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const maxAllowed = getMaxImages(newPost.platform);
    const currentCount = newPost.image_urls.length;
    const remaining = maxAllowed - currentCount;
    
    if (remaining <= 0) {
      toast.error(t.distribution.maxImages.replace('{{max}}', maxAllowed));
      return;
    }

    const filesToUpload = files.slice(0, remaining);

    for (const file of filesToUpload) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} ${t.distribution.imageNotFile}`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ${t.distribution.imageTooLarge}`);
        continue;
      }

      setIsUploadingImage(true);
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        });

        setNewPost(prev => ({ ...prev, image_urls: [...prev.image_urls, response.data.url] }));
      } catch (err) {
        toast.error(`${t.distribution.uploadFailed}: ${file.name}`);
      } finally {
        setIsUploadingImage(false);
      }
    }
    toast.success(`${filesToUpload.length} ${t.distribution.imagesUploaded}`);
    // Reset file input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index)
    }));
  };

  // Handle video upload for new post
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mpeg', 'video/x-matroska'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t.distribution.onlyVideoAllowed);
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error(t.distribution.videoTooLarge);
      return;
    }

    setIsUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/video`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      setNewPost(prev => ({ ...prev, video_url: response.data.url }));
      toast.success(t.distribution.videoUploaded);
    } catch (err) {
      toast.error(t.distribution.videoUploadFailed);
    } finally {
      setIsUploadingVideo(false);
      e.target.value = '';
    }
  };

  const removeVideo = () => {
    setNewPost(prev => ({ ...prev, video_url: '' }));
  };

  const handleCreatePost = async () => {
    if (!newPost.platform || !newPost.scheduled_at) {
      toast.error(t.distribution.fillRequiredFields);
      return;
    }

    // Press distribution gate — growth/custom only.
    // Show upgrade popup whenever a user without press_distribution feature
    // tries to schedule a "Pressemitteilung" event.
    if (newPost.platform === 'pressemitteilung' && !subscription.features?.press_distribution) {
      setShowPressGate(true);
      return;
    }

    // Pinterest validation
    if (newPost.platform === 'pinterest') {
      if (!newPost.pin_title) {
        toast.error(t.distribution.pinTitleRequired);
        return;
      }
      if (newPost.pin_title.length > 100) {
        toast.error(t.distribution.pinTitleTooLong);
        return;
      }
      if (newPost.pin_description.length > 500) {
        toast.error(t.distribution.pinDescTooLong);
        return;
      }
      if (newPost.image_urls.length === 0) {
        toast.error(t.distribution.pinImageRequired);
        return;
      }
      if (pinterestBoards.length > 0 && !newPost.pinterest_board_id) {
        toast.error(t.distribution.selectPinterestBoard);
        return;
      }
    } else if (!newPost.text_content) {
      toast.error(t.distribution.textRequired);
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        platform: newPost.platform,
        text_content: newPost.platform === 'pinterest' 
          ? `${newPost.pin_title}\n\n${newPost.pin_description}`
          : newPost.text_content,
        scheduled_at: newPost.scheduled_at,
        image_urls: newPost.image_urls,
      };
      
      // Add video URL if present
      if (newPost.video_url) {
        payload.video_url = newPost.video_url;
      }

      // Add Pinterest-specific fields
      if (newPost.platform === 'pinterest') {
        payload.pin_title = newPost.pin_title;
        payload.pin_description = newPost.pin_description;
        if (newPost.pin_link) payload.pin_link = newPost.pin_link;
        if (newPost.pinterest_board_id) payload.pinterest_board_id = newPost.pinterest_board_id;
      }

      const response = await axios.post(`${API}/distribution/events`, payload, {
        headers: getAuthHeader()
      });
      setEvents(prev => [...prev, response.data.event]);
      setShowNewPostModal(false);
      setNewPost({ platform: '', text_content: '', image_urls: [], video_url: '', scheduled_at: '', pin_title: '', pin_description: '', pin_link: '', pinterest_board_id: '' });
      toast.success(t.distribution.postCreated);
    } catch (err) {
      toast.error(t.distribution.postCreateFailed);
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditEvent = async () => {
    if (!selectedEvent) return;
    setIsSaving(true);

    try {
      const response = await axios.put(
        `${API}/distribution/events/${selectedEvent.id}`,
        editForm,
        { headers: getAuthHeader() }
      );
      
      setEvents(prev => prev.map(e => e.id === selectedEvent.id ? { ...e, ...response.data.event } : e));
      setSelectedEvent(prev => ({ ...prev, ...response.data.event }));
      setShowEditModal(false);
      toast.success(t.distribution.changesSaved);
    } catch (err) {
      toast.error(t.distribution.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedEvent) return;

    // Open duplicate dialog with pre-filled data
    setDuplicateForm({
      platform: selectedEvent.platform,
      scheduled_at: selectedEvent.scheduled_at?.slice(0, 16) || ''
    });
    setShowDuplicateModal(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!selectedEvent || !duplicateForm.scheduled_at) {
      toast.error(t.distribution.provideDatetime);
      return;
    }
    setIsDuplicating(true);
    try {
      const response = await axios.post(
        `${API}/distribution/events/${selectedEvent.id}/duplicate`,
        {
          platform: duplicateForm.platform,
          scheduled_at: duplicateForm.scheduled_at
        },
        { headers: getAuthHeader() }
      );
      setEvents(prev => [...prev, response.data.event]);
      setShowDuplicateModal(false);
      toast.success(t.distribution.duplicated);
    } catch (err) {
      toast.error(t.distribution.duplicateFailed);
    } finally {
      setIsDuplicating(false);
    }
  };

  const handlePublishLinkedin = async (eventId) => {
    setPublishingLinkedin(eventId);
    try {
      const response = await axios.post(
        `${API}/distribution/events/${eventId}/publish-linkedin`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message || t.distribution.linkedinPosted);
      // Update event in state
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, linkedin_posted: true, status: 'posted' } : e));
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(prev => ({ ...prev, linkedin_posted: true, status: 'posted' }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t.distribution.linkedinFailed);
    } finally {
      setPublishingLinkedin(null);
    }
  };

  const handleApproveLinkedin = async (eventId, approved) => {
    try {
      await axios.post(
        `${API}/distribution/events/${eventId}/schedule-linkedin`,
        { approved },
        { headers: getAuthHeader() }
      );
      const msg = approved ? 'Für LinkedIn freigegeben' : 'LinkedIn-Freigabe zurückgezogen';
      toast.success(msg);
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, linkedin_approved: approved } : e));
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(prev => ({ ...prev, linkedin_approved: approved }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handlePublishPinterest = async (eventId, boardId) => {
    if (!boardId) {
      toast.error(t.distribution.selectPinterestBoard);
      return;
    }
    setPublishingPinterest(eventId);
    try {
      const response = await axios.post(
        `${API}/distribution/events/${eventId}/publish-pinterest`,
        { board_id: boardId },
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message || t.distribution.pinterestPosted);
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, pinterest_posted: true, status: 'posted' } : e));
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(prev => ({ ...prev, pinterest_posted: true, status: 'posted' }));
      }
      setShowPinterestPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.distribution.pinterestFailed);
    } finally {
      setPublishingPinterest(null);
    }
  };

  const handleApprovePinterest = async (eventId, approved, boardId) => {
    try {
      await axios.post(
        `${API}/distribution/events/${eventId}/schedule-pinterest`,
        { approved, board_id: boardId },
        { headers: getAuthHeader() }
      );
      const msg = approved ? 'Für Pinterest freigegeben' : 'Pinterest-Freigabe zurückgezogen';
      toast.success(msg);
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, pinterest_approved: approved } : e));
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(prev => ({ ...prev, pinterest_approved: approved }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    try {
      const response = await axios.delete(
        `${API}/distribution/events/${selectedEvent.id}`,
        { headers: getAuthHeader() }
      );
      
      setDeletedEvent(response.data.deleted_event);
      setEvents(prev => prev.filter(e => e.id !== selectedEvent.id));
      setSelectedEvent(null);
      
      toast.success(
        <div className="flex items-center gap-2">
          <span>Gelöscht</span>
          <button 
            onClick={() => handleUndoDelete(response.data.deleted_event)}
            className="text-[#00D9C8] hover:underline ml-2"
          >
            Rückgängig
          </button>
        </div>,
        { duration: 5000 }
      );

      setTimeout(() => setDeletedEvent(null), 5000);
    } catch (err) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handleReschedule = async (eventId) => {
    if (!rescheduleDate) return;
    setRescheduling(true);
    try {
      const formData = new FormData();
      formData.append('new_date', rescheduleDate);
      const response = await axios.post(
        `${API}/distribution/events/${eventId}/reschedule`,
        formData,
        { headers: getAuthHeader() }
      );
      const updatedEvent = response.data.event;
      setEvents(prev => prev.map(e => e.id === eventId ? updatedEvent : e));
      setSelectedEvent(updatedEvent);
      setRescheduleDate('');
      if (updatedEvent.status === 'failed') {
        toast.error(t.distribution.rescheduledFailed);
      } else {
        toast.success(t.distribution.rescheduled);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t.distribution.rescheduleFailed);
    } finally {
      setRescheduling(false);
    }
  };

  const handleUndoDelete = async (event) => {
    if (!event) return;

    try {
      // Recreate the event
      const response = await axios.post(`${API}/distribution/events`, {
        platform: event.platform,
        scheduled_at: event.scheduled_at,
        text_content: event.manual_text || '',
        image_urls: event.manual_image_urls || (event.manual_image_url ? [event.manual_image_url] : []),
        video_url: event.manual_video_url || undefined
      }, { headers: getAuthHeader() });
      
      setEvents(prev => [...prev, response.data.event]);
      setDeletedEvent(null);
      toast.success(t.distribution.restored);
    } catch (err) {
      toast.error('Wiederherstellung fehlgeschlagen');
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    setOverId(event.over?.id || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over || !active) return;

    const draggedEvent = events.find(e => e.id === active.id);
    if (!draggedEvent) return;

    // Get target date from droppable id (format: "day-YYYY-MM-DD")
    const targetDateKey = over.id;
    if (!targetDateKey || !targetDateKey.toString().startsWith('day-')) return;

    const targetDateStr = targetDateKey.toString().replace('day-', '');
    const [year, month, day] = targetDateStr.split('-').map(Number);
    
    // Parse original time
    const originalDate = new Date(draggedEvent.scheduled_at);
    const hours = originalDate.getHours();
    const minutes = originalDate.getMinutes();
    
    // Create new date with same time
    const newDate = new Date(year, month - 1, day, hours, minutes);
    
    // Check if it's the same day
    const originalDateStr = `${originalDate.getFullYear()}-${String(originalDate.getMonth() + 1).padStart(2, '0')}-${String(originalDate.getDate()).padStart(2, '0')}`;
    if (originalDateStr === targetDateStr) return; // No change needed

    try {
      // Update via API
      const response = await axios.put(
        `${API}/distribution/events/${draggedEvent.id}`,
        { scheduled_at: newDate.toISOString() },
        { headers: getAuthHeader() }
      );

      // Update local state
      setEvents(prev => prev.map(e => 
        e.id === draggedEvent.id 
          ? { ...e, scheduled_at: newDate.toISOString() }
          : e
      ));

      toast.success(`Verschoben auf ${day}. ${monthNames[month - 1]}`);
    } catch (err) {
      toast.error('Verschieben fehlgeschlagen');
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };

  // Get active dragged event
  const activeEvent = activeId ? events.find(e => e.id === activeId) : null;

  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setEditForm({
      scheduled_at: event.scheduled_at?.slice(0, 16) || '',
      text_content: getEventText(event),
      image_urls: getEventImages(event),
      video_url: event.manual_video_url || ''
    });
  };

  // Get unique platforms in current view
  const activePlatforms = useMemo(() => {
    const platformKeys = [...new Set(events.map(e => e.platform))];
    return platforms.filter(p => platformKeys.includes(p.key));
  }, [events]);

  // Week view data
  const weekDays7 = useMemo(() => {
    const startOfWeek = getStartOfWeek(currentDate);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + i);
      return date;
    });
  }, [currentDate]);

  // Month view data
  const monthCalendar = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Adjust for Monday start
    let startDay = firstDay.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;
    
    const calendar = [];
    let week = [];
    
    // Empty cells before first day
    for (let i = 0; i < startDay; i++) {
      week.push(null);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      week.push(new Date(year, month, day));
      if (week.length === 7) {
        calendar.push(week);
        week = [];
      }
    }
    
    // Fill remaining cells
    if (week.length > 0) {
      while (week.length < 7) {
        week.push(null);
      }
      calendar.push(week);
    }
    
    return calendar;
  }, [currentDate]);

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="h-full flex flex-col" data-testid="distribution-page">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 lg:mb-6">
        <div>
          <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-1 lg:mb-2">
            {t.distribution.title}
          </h1>
          <p className="text-sm lg:text-base text-[#A0AEC0]">
            {t.distribution.subtitle}
          </p>
        </div>
        
        {/* Controls - Stacked on mobile */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4">
          {/* View Toggle & Navigation Row */}
          <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-4">
            {/* View Toggle */}
            <div className="flex bg-[#1A2B42] p-1">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm font-medium transition-colors ${
                  viewMode === 'week' 
                    ? 'bg-[#00D9C8] text-[#0A1628]' 
                    : 'text-[#A0AEC0] hover:text-white'
                }`}
                data-testid="week-view-btn"
              >
                {t.distribution.week}
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm font-medium transition-colors ${
                  viewMode === 'month' 
                    ? 'bg-[#00D9C8] text-[#0A1628]' 
                    : 'text-[#A0AEC0] hover:text-white'
                }`}
                data-testid="month-view-btn"
              >
                {t.distribution.month}
              </button>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-1 lg:gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={navigatePrev}
                className="text-[#A0AEC0] hover:text-white hover:bg-[#1A2B42] w-8 h-8 lg:w-10 lg:h-10"
                data-testid="prev-btn"
              >
                <ChevronLeft className="w-4 h-4 lg:w-5 lg:h-5" />
              </Button>
              <span className="text-white font-medium text-xs lg:text-base min-w-[100px] lg:min-w-[200px] text-center">
                {getPeriodLabel()}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={navigateNext}
                className="text-[#A0AEC0] hover:text-white hover:bg-[#1A2B42] w-8 h-8 lg:w-10 lg:h-10"
                data-testid="next-btn"
              >
                <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" />
              </Button>
            </div>
          </div>

          {/* New Post Button */}
          <Button
            onClick={() => setShowNewPostModal(true)}
            className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold text-sm lg:text-base h-9 lg:h-10"
            data-testid="new-post-btn"
          >
            <Plus className="w-4 h-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">{t.distribution.newPost}</span>
            <span className="sm:hidden">{t.distribution.newPost}</span>
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00D9C8]" />
        </div>
      )}

      {/* Week View with Drag and Drop */}
      {!isLoading && viewMode === 'week' && (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="flex-1 grid grid-cols-7 gap-1 lg:gap-3 overflow-x-auto">
            {weekDays7.map((date, index) => {
              const dayEvents = getEventsForDate(date);
              const showMore = dayEvents.length > 2;
              const displayEvents = expandedDay === index ? dayEvents : dayEvents.slice(0, 2);
              const dateKey = `day-${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const isOverThisDay = overId === dateKey;
              
              return (
                <div
                  key={index}
                  className={`flex flex-col bg-[#1A2B42] min-h-[300px] lg:min-h-[450px] min-w-[60px] lg:min-w-0 ${isToday(date) ? 'ring-2 ring-[#00D9C8]' : ''}`}
                >
                  {/* Day Header - Mobile uses short names */}
                  <div className="p-1.5 lg:p-3 border-b border-[#0A1628] text-center">
                    <p className="text-[10px] lg:text-xs text-[#5A6C7D] hidden lg:block">{weekDaysFull[index]}</p>
                    <p className="text-[10px] lg:text-xs text-[#5A6C7D] lg:hidden">{weekDays[index]}</p>
                    <p className={`text-sm lg:text-xl font-bold ${isToday(date) ? 'text-[#00D9C8]' : 'text-white'}`}>
                      {date.getDate()}
                    </p>
                  </div>

                  {/* Droppable Events Area */}
                  <DroppableDay 
                    date={date} 
                    dateKey={dateKey}
                    isToday={isToday(date)}
                    isOver={isOverThisDay}
                  >
                    {displayEvents.map(event => {
                      const platform = getPlatformConfig(event.platform);
                      
                      return (
                        <DraggableEvent
                          key={event.id}
                          event={event}
                          platform={platform}
                          formatTime={formatTime}
                          getEventTitle={getEventTitle}
                          getEventText={getEventText}
                          getEventImage={getEventImage}
                          onClick={() => openEventDetail(event)}
                          onRetry={handleRetrySync}
                          lang={lang}
                        />
                      );
                    })}

                    {showMore && expandedDay !== index && (
                      <button
                        onClick={() => setExpandedDay(index)}
                        className="w-full text-[10px] lg:text-xs text-[#00D9C8] hover:underline py-1"
                      >
                        +{dayEvents.length - 2}
                      </button>
                    )}

                    {expandedDay === index && dayEvents.length > 2 && (
                      <button
                        onClick={() => setExpandedDay(null)}
                        className="w-full text-[10px] lg:text-xs text-[#5A6C7D] hover:text-white py-1"
                      >
                        ↑
                      </button>
                    )}
                    
                    {/* Empty state - Hidden on mobile for space */}
                    {dayEvents.length === 0 && (
                      <div className="flex-1 flex items-center justify-center py-4 lg:py-8">
                        <p className="text-[10px] lg:text-xs text-[#5A6C7D] hidden lg:block">Keine Posts</p>
                        <p className="text-[8px] text-[#5A6C7D] lg:hidden">-</p>
                      </div>
                    )}
                  </DroppableDay>
                </div>
              );
            })}
          </div>

          {/* Drag Overlay - Shows dragged item */}
          <DragOverlay>
            {activeEvent && (
              <div 
                className="w-[200px] shadow-2xl opacity-90"
                style={{ 
                  backgroundColor: `${getPlatformConfig(activeEvent.platform).color}20`,
                  border: `2px solid ${getPlatformConfig(activeEvent.platform).color}`
                }}
              >
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-1">
                    {(() => {
                      const platform = getPlatformConfig(activeEvent.platform);
                      const Icon = platform.icon;
                      return <Icon className="w-3 h-3" style={{ color: platform.color }} />;
                    })()}
                    <span className="text-[10px] text-[#5A6C7D]">
                      {formatTime(activeEvent.scheduled_at)}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-white line-clamp-2">
                    {getEventTitle(activeEvent) || getEventText(activeEvent).substring(0, 50)}
                  </p>
                </div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Month View */}
      {!isLoading && viewMode === 'month' && (
        <div className="flex-1">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekDays.map(day => (
              <div key={day} className="p-2 text-center text-xs font-medium text-[#5A6C7D]">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 flex-1">
            {monthCalendar.flat().map((date, index) => {
              if (!date) {
                return <div key={index} className="bg-[#0A1628]/50 p-2 min-h-[140px]" />;
              }

              const dayEvents = getEventsForDate(date);
              const displayEvents = dayEvents.slice(0, 2);
              const hasMore = dayEvents.length > 2;

              return (
                <Popover key={index}>
                  <PopoverTrigger asChild>
                    <button
                      className={`bg-[#1A2B42] p-2 min-h-[140px] text-left transition-colors hover:bg-[#1A2B42]/80 flex flex-col ${
                        isToday(date) ? 'ring-2 ring-[#00D9C8]' : ''
                      }`}
                      data-testid={`day-${date.getDate()}`}
                    >
                      <p className={`text-sm font-medium mb-2 ${isToday(date) ? 'text-[#00D9C8]' : 'text-white'}`}>
                        {date.getDate()}
                      </p>
                      <div className="flex-1 space-y-1 overflow-hidden">
                        {displayEvents.map(event => {
                          const platform = getPlatformConfig(event.platform);
                          const Icon = platform.icon;
                          const imageUrl = getEventImage(event);
                          const title = getEventTitle(event);
                          
                          return (
                            <div
                              key={event.id}
                              className="flex gap-1.5 items-start p-1 overflow-hidden"
                              style={{ backgroundColor: `${platform.color}15` }}
                            >
                              {/* Mini Image or Platform Icon */}
                              {imageUrl ? (
                                <div className="w-8 h-8 flex-shrink-0 overflow-hidden">
                                  <img 
                                    src={imageUrl} 
                                    alt="" 
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : (
                                <div 
                                  className="w-8 h-8 flex-shrink-0 flex items-center justify-center"
                                  style={{ backgroundColor: `${platform.color}20` }}
                                >
                                  <Icon className="w-3 h-3" style={{ color: platform.color }} />
                                </div>
                              )}
                              {/* Title - only for headline platforms */}
                              <p className="text-[10px] text-white line-clamp-2 leading-tight flex-1">
                                {title || getEventText(event).substring(0, 50)}
                              </p>
                            </div>
                          );
                        })}
                        {hasMore && (
                          <p className="text-[10px] text-[#00D9C8]">+{dayEvents.length - 2} mehr</p>
                        )}
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 bg-[#1A2B42] border-[#0A1628] p-2">
                    <p className="text-sm font-medium text-white mb-2">
                      {date.getDate()}. {monthNames[date.getMonth()]}
                    </p>
                    {dayEvents.length === 0 ? (
                      <p className="text-xs text-[#5A6C7D]">Keine Postings</p>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {dayEvents.map(event => {
                          const platform = getPlatformConfig(event.platform);
                          const Icon = platform.icon;
                          const imageUrl = getEventImage(event);
                          const title = getEventTitle(event);
                          const text = getEventText(event);
                          const previewText = text.split(/[.!?]+/).filter(s => s.trim()).slice(0, 2).join('. ').trim();
                          
                          return (
                            <button
                              key={event.id}
                              onClick={() => openEventDetail(event)}
                              className="w-full text-left bg-[#0A1628] hover:bg-[#0A1628]/80 transition-colors overflow-hidden"
                            >
                              {/* Image */}
                              {imageUrl && (
                                <div className="w-full h-16 overflow-hidden">
                                  <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="p-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <Icon className="w-3 h-3" style={{ color: platform.color }} />
                                  <span className="text-[10px] text-[#5A6C7D]">
                                    {formatTime(event.scheduled_at)}
                                  </span>
                                </div>
                                {title && (
                                  <p className="text-xs text-white font-medium line-clamp-1 mb-1">
                                    {title}
                                  </p>
                                )}
                                {previewText && (
                                  <p className="text-[10px] text-[#A0AEC0] line-clamp-2">
                                    {previewText.substring(0, 80)}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        </div>
      )}

      {/* Platform Legend */}
      {activePlatforms.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#1A2B42]">
          <div className="flex items-center gap-4 flex-wrap">
            {activePlatforms.map(platform => {
              const Icon = platform.icon;
              return (
                <div key={platform.key} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: platform.color }} />
                  <Icon className="w-4 h-4" style={{ color: platform.color }} />
                  <span className="text-xs text-[#A0AEC0]">{platform.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail Side Panel - Full screen on mobile */}
      <AnimatePresence>
        {selectedEvent && (
          <>
            {/* Backdrop for mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setSelectedEvent(null)}
            />
            <motion.div
              initial={{ x: 400 }}
              animate={{ x: 0 }}
              exit={{ x: 400 }}
              className="fixed right-0 top-0 bottom-0 w-full lg:w-[400px] bg-[#0A1628] border-l border-[#1A2B42] overflow-y-auto z-50"
              data-testid="event-detail-panel"
            >
              {/* Panel Header */}
              <div className="sticky top-0 bg-[#0A1628] border-b border-[#1A2B42] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => {
                    const platform = getPlatformConfig(selectedEvent.platform);
                    const Icon = platform.icon;
                    return (
                      <>
                        <Icon className="w-5 h-5" style={{ color: platform.color }} />
                        <span className="font-bold text-white">{platform.label}</span>
                      </>
                    );
                  })()}
                </div>
                <button 
                  onClick={() => setSelectedEvent(null)}
                  className="p-2 text-[#A0AEC0] hover:text-white"
                  data-testid="close-detail-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Panel Content */}
              <div className="p-4 space-y-4">
                {/* Scheduled Date/Time */}
                <div>
                  <Label className="text-[#A0AEC0] text-xs mb-2 block">Geplant für</Label>
                  <div className="flex items-center gap-2 text-white text-sm">
                    <CalendarIcon className="w-4 h-4 text-[#00D9C8]" />
                    <span>{formatDate(selectedEvent.scheduled_at)}</span>
                    <Clock className="w-4 h-4 text-[#00D9C8] ml-2" />
                    <span>{formatTime(selectedEvent.scheduled_at)}</span>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <Label className="text-[#A0AEC0] text-xs mb-2 block">Status</Label>
                  {(() => {
                    const zs = selectedEvent.outstand_sync_status;
                    const firstUrl = selectedEvent.outstand_post_url || '';
                    if (zs === 'posted') {
                      return (
                        <div className="flex items-center gap-2 flex-wrap" data-testid="detail-status-posted">
                          <Badge className="bg-green-500/20 text-green-400">{lang === 'de' ? 'Veröffentlicht' : 'Published'}</Badge>
                          {firstUrl && (
                            <a
                              href={firstUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-green-400 hover:text-green-300 underline inline-flex items-center gap-1"
                              data-testid="detail-view-post-link"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {lang === 'de' ? 'View Post' : 'View Post'}
                            </a>
                          )}
                        </div>
                      );
                    }
                    if (zs === 'publishing') {
                      return <Badge className="bg-blue-500/20 text-blue-400 animate-pulse" data-testid="detail-status-publishing">{lang === 'de' ? 'Wird veröffentlicht…' : 'Publishing…'}</Badge>;
                    }
                    if (zs === 'failed') {
                      return (
                        <div className="space-y-2" data-testid="detail-status-failed">
                          <Badge className="bg-red-500/20 text-red-400">{lang === 'de' ? 'Fehlgeschlagen' : 'Failed'}</Badge>
                          {selectedEvent.outstand_error && (
                            <p className="text-xs text-red-300/80">{selectedEvent.outstand_error}</p>
                          )}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleRetrySync(selectedEvent)}
                              className="bg-[#00D9C8] hover:bg-[#00D9C8]/80 text-[#0A1628] h-8 text-xs"
                              data-testid="detail-retry-btn"
                            >
                              {lang === 'de' ? 'Nochmal probieren' : 'Retry'}
                            </Button>
                            <a href="mailto:hello@xchannels.ai" className="text-xs text-[#A0AEC0] underline hover:text-white">
                              {lang === 'de' ? 'Support kontaktieren' : 'Contact support'}
                            </a>
                          </div>
                        </div>
                      );
                    }
                    if (zs === 'scheduled' || selectedEvent.outstand_post_id) {
                      return <Badge className="bg-[#00D9C8]/20 text-[#00D9C8]" data-testid="detail-status-scheduled">{lang === 'de' ? 'Eingeplant' : 'Scheduled'}</Badge>;
                    }
                    if (!selectedEvent.outstand_post_id) {
                      return <Badge className="bg-gray-500/20 text-gray-400" data-testid="detail-status-none">{lang === 'de' ? 'Nicht synchronisiert — kein Kanal' : 'Not synced — no channel'}</Badge>;
                    }
                    return (
                      <Badge className={statusConfig[selectedEvent.status]?.color || 'bg-gray-500/20 text-gray-500'}>
                        {statusConfig[selectedEvent.status]?.label || selectedEvent.status}
                      </Badge>
                    );
                  })()}
                </div>

                {/* Outstand Error + Reschedule */}
                {selectedEvent.outstand_error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 space-y-3" data-testid="outstand-error-box">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-400">{t.distribution.postingFailed}</p>
                        <p className="text-xs text-red-300/80 mt-1">{selectedEvent.outstand_error}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="datetime-local"
                        value={rescheduleDate}
                        onChange={(e) => setRescheduleDate(e.target.value)}
                        className="bg-[#1A2B42] border-red-500/30 text-white text-sm h-9 flex-1"
                        data-testid="reschedule-date-input"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleReschedule(selectedEvent.id)}
                        disabled={!rescheduleDate || rescheduling}
                        className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 h-9"
                        data-testid="reschedule-btn"
                      >
                        {rescheduling ? <Loader2 className="w-3 h-3 animate-spin" /> : t.distribution.reschedule}
                      </Button>
                    </div>
                  </div>
                )}

              {/* Text Preview - Pinterest shows separate fields */}
              {selectedEvent.platform === 'pinterest' ? (
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-[#A0AEC0] text-xs">Pin-Titel</Label>
                      <span className="text-[10px] text-[#5A6C7D]">
                        {getPinTitle(selectedEvent).length}/100
                      </span>
                    </div>
                    <div className="bg-[#1A2B42] p-3 rounded">
                      <p className="text-sm text-white font-medium">
                        {getPinTitle(selectedEvent) || <span className="text-[#5A6C7D] italic">Kein Titel vorhanden</span>}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-[#A0AEC0] text-xs">Pin-Beschreibung</Label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(getPinDescription(selectedEvent));
                          toast.success('Beschreibung kopiert!');
                        }}
                        className="h-7 px-2 text-[#00D9C8] hover:bg-[#00D9C8]/10"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Kopieren
                      </Button>
                    </div>
                    <div className="bg-[#1A2B42] p-3 rounded max-h-[150px] overflow-y-auto">
                      <p className="text-sm text-white whitespace-pre-wrap">
                        {getPinDescription(selectedEvent) || <span className="text-[#5A6C7D] italic">Keine Beschreibung vorhanden</span>}
                      </p>
                    </div>
                  </div>
                  {/* Pin Link */}
                  {(selectedEvent.pin_link || selectedEvent.manual_pin_link || selectedEvent.content?.structured_content?.pin_link) && (
                    <div>
                      <Label className="text-[#A0AEC0] text-xs mb-2 block">Link</Label>
                      <a 
                        href={selectedEvent.pin_link || selectedEvent.manual_pin_link || selectedEvent.content?.structured_content?.pin_link}
                        target="_blank" rel="noopener noreferrer"
                        className="text-sm text-[#00D9C8] hover:underline break-all"
                      >
                        {selectedEvent.pin_link || selectedEvent.manual_pin_link || selectedEvent.content?.structured_content?.pin_link}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[#A0AEC0] text-xs">Text</Label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(getEventText(selectedEvent));
                      toast.success('Text kopiert!');
                    }}
                    className="h-7 px-2 text-[#00D9C8] hover:bg-[#00D9C8]/10"
                    data-testid="copy-text-btn"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Kopieren
                  </Button>
                </div>
                <div className="bg-[#1A2B42] p-4 max-h-[200px] overflow-y-auto">
                  <p className="text-sm text-white whitespace-pre-wrap">
                    {getEventText(selectedEvent)}
                  </p>
                </div>
              </div>
              )}

              {/* Image Gallery */}
              {getEventImages(selectedEvent).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#A0AEC0] text-xs">
                      {getEventImages(selectedEvent).length === 1 ? 'Bild' : `Bilder (${getEventImages(selectedEvent).length})`}
                    </Label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        getEventImages(selectedEvent).forEach((url, idx) => {
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${selectedEvent.platform}-${selectedEvent.id}-${idx + 1}.jpg`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        });
                        toast.success('Bilder werden heruntergeladen...');
                      }}
                      className="h-7 px-2 text-[#00D9C8] hover:bg-[#00D9C8]/10"
                      data-testid="download-image-btn"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Herunterladen
                    </Button>
                  </div>
                  {getEventImages(selectedEvent).length === 1 ? (
                    <img 
                      src={getEventImages(selectedEvent)[0]} 
                      alt="" 
                      className="w-full aspect-video object-cover bg-[#1A2B42] rounded"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-1.5">
                      {getEventImages(selectedEvent).map((url, idx) => (
                        <div key={idx} className="relative">
                          <img 
                            src={url} 
                            alt="" 
                            className="w-full aspect-square object-cover bg-[#1A2B42] rounded"
                          />
                          <div className="absolute top-1 left-1 w-5 h-5 bg-[#00D9C8] rounded-full flex items-center justify-center text-[10px] font-bold text-[#0A1628]">
                            {idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Video Display */}
              {selectedEvent.manual_video_url && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#A0AEC0] text-xs flex items-center gap-1.5">
                      <Film className="w-3.5 h-3.5" />
                      Video
                    </Label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = selectedEvent.manual_video_url;
                        link.download = `${selectedEvent.platform}-${selectedEvent.id}-video.mp4`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success(t.distribution.downloadingVideo);
                      }}
                      className="h-7 px-2 text-[#00D9C8] hover:bg-[#00D9C8]/10"
                      data-testid="download-video-btn"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      {t.common.download}
                    </Button>
                  </div>
                  <video 
                    src={selectedEvent.manual_video_url} 
                    className="w-full rounded bg-black"
                    controls
                    data-testid="event-video-player"
                  />
                </div>
              )}

              {/* Link to Produktion (for non-manual) */}
              {!selectedEvent.is_manual && selectedEvent.content_piece_id && (
                <Link
                  to={`/app/produktion?content=${selectedEvent.content_piece_id}`}
                  className="flex items-center gap-2 text-[#00D9C8] text-sm hover:underline"
                  data-testid="view-in-produktion-link"
                >
                  In Produktion ansehen
                  <ExternalLink className="w-4 h-4" />
                </Link>
              )}
            </div>

            {/* Action Buttons */}
            <div className="sticky bottom-0 bg-[#0A1628] border-t border-[#1A2B42] p-4 space-y-2">
              {/* Generic Auto-Post Status for ALL platforms */}
              {(() => {
                const plat = platforms.find(p => p.key === selectedEvent.platform);
                const platLabel = plat?.label || selectedEvent.platform;
                const platColor = plat?.color || '#5A6C7D';
                const PlatIcon = plat?.icon;
                const eventIsPosted = selectedEvent.linkedin_posted || selectedEvent.pinterest_posted || selectedEvent.status === 'posted' || (selectedEvent.scheduled_at && new Date(selectedEvent.scheduled_at) < new Date());
                
                return (
                  <div className="mb-2 bg-[#0F1D32] rounded-lg border border-[#1A2B42] overflow-hidden" data-testid="auto-post-section">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1A2B42]">
                      {PlatIcon && <PlatIcon className="w-4 h-4" style={{ color: platColor }} />}
                      <span className="text-white text-sm font-medium">Automatisch bei {platLabel} posten</span>
                    </div>
                    <div className="px-3 py-3">
                      {eventIsPosted ? (
                        <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 p-2.5 rounded-lg font-medium" data-testid="auto-post-posted-badge">
                          <Check className="w-4 h-4" />
                          Gepostet
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-[#00D9C8] bg-[#00D9C8]/10 p-2.5 rounded-lg">
                            <Send className="w-4 h-4" />
                            <span>Posting geplant für <strong>{selectedEvent.scheduled_at ? new Date(selectedEvent.scheduled_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–'}</strong></span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* LinkedIn Actions */}
              {linkedinConnected && selectedEvent.platform === 'linkedin' && (
                <div className="mb-2" data-testid="linkedin-actions">
                  {selectedEvent.linkedin_posted ? (
                    <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 p-3 rounded-lg font-medium" data-testid="linkedin-posted-badge">
                      <Check className="w-5 h-5" />
                      Auf LinkedIn gepostet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        onClick={() => setShowLinkedinPreview(selectedEvent)}
                        disabled={publishingLinkedin === selectedEvent.id}
                        className="w-full bg-[#0A66C2] hover:bg-[#0A66C2]/80 text-white h-12 text-base font-semibold"
                        data-testid="post-linkedin-main-btn"
                      >
                        {publishingLinkedin === selectedEvent.id ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <FaLinkedin className="w-5 h-5 mr-2" />
                        )}
                        Auf LinkedIn posten
                      </Button>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => setShowLinkedinPreview(selectedEvent)}
                          disabled={publishingLinkedin === selectedEvent.id}
                          variant="outline"
                          className="border-[#0A66C2]/50 text-[#0A66C2] hover:bg-[#0A66C2]/10 text-xs"
                          data-testid="post-linkedin-now-btn"
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />
                          Jetzt posten
                        </Button>
                        <Button
                          onClick={() => handleApproveLinkedin(selectedEvent.id, !selectedEvent.linkedin_approved)}
                          variant="outline"
                          className={`text-xs ${selectedEvent.linkedin_approved 
                            ? 'border-green-500/50 text-green-400 bg-green-500/10 hover:bg-green-500/20' 
                            : 'border-[#0A66C2]/50 text-[#0A66C2] hover:bg-[#0A66C2]/10'}`}
                          data-testid="schedule-linkedin-btn"
                        >
                          {selectedEvent.linkedin_approved ? (
                            <><Check className="w-3.5 h-3.5 mr-1" /> Geplant</>
                          ) : (
                            <><Clock className="w-3.5 h-3.5 mr-1" /> Zum geplanten Zeitpunkt</>
                          )}
                        </Button>
                      </div>
                      {selectedEvent.linkedin_approved && !selectedEvent.linkedin_posted && (
                        <p className="text-[10px] text-green-400 bg-green-500/5 p-2 rounded text-center">
                          Wird automatisch am {new Date(selectedEvent.scheduled_at).toLocaleString('de-DE')} gepostet
                        </p>
                      )}
                    </div>
                  )}
                  {selectedEvent.linkedin_error && (
                    <p className="text-[10px] text-red-400 mt-1">
                      Fehler: {selectedEvent.linkedin_error}
                    </p>
                  )}
                </div>
              )}

              {/* Pinterest Actions */}
              {pinterestConnected && selectedEvent.platform === 'pinterest' && (
                <div className="mb-2" data-testid="pinterest-actions">
                  {selectedEvent.pinterest_posted ? (
                    <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 p-3 rounded-lg font-medium" data-testid="pinterest-posted-badge">
                      <Check className="w-5 h-5" />
                      Auf Pinterest gepostet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {getEventImages(selectedEvent).length === 0 && (
                        <div className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded">
                          Pinterest Pins benötigen mindestens ein Bild
                        </div>
                      )}
                      {getEventImages(selectedEvent).length > 0 && (
                        <>
                          {pinterestBoards.length > 0 && (
                            <select
                              value={selectedBoardId}
                              onChange={(e) => setSelectedBoardId(e.target.value)}
                              className="w-full bg-[#0A1628] border border-[#1A2B42] rounded px-2 py-1.5 text-xs text-white focus:border-[#BD081C] outline-none"
                              data-testid="pinterest-board-inline-select"
                            >
                              {pinterestBoards.map((board) => (
                                <option key={board.id} value={board.id}>{board.name}</option>
                              ))}
                            </select>
                          )}
                          <Button
                            onClick={() => setShowPinterestPreview(selectedEvent)}
                            disabled={publishingPinterest === selectedEvent.id}
                            className="w-full bg-[#BD081C] hover:bg-[#BD081C]/80 text-white h-12 text-base font-semibold"
                            data-testid="post-pinterest-main-btn"
                          >
                            {publishingPinterest === selectedEvent.id ? (
                              <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            ) : (
                              <FaPinterest className="w-5 h-5 mr-2" />
                            )}
                            Auf Pinterest posten
                          </Button>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              onClick={() => setShowPinterestPreview(selectedEvent)}
                              disabled={publishingPinterest === selectedEvent.id}
                              variant="outline"
                              className="border-[#BD081C]/50 text-[#BD081C] hover:bg-[#BD081C]/10 text-xs"
                              data-testid="post-pinterest-now-btn"
                            >
                              <ExternalLink className="w-3.5 h-3.5 mr-1" />
                              Jetzt posten
                            </Button>
                            <Button
                              onClick={() => handleApprovePinterest(selectedEvent.id, !selectedEvent.pinterest_approved, selectedBoardId)}
                              variant="outline"
                              disabled={!selectedBoardId}
                              className={`text-xs ${selectedEvent.pinterest_approved 
                                ? 'border-green-500/50 text-green-400 bg-green-500/10 hover:bg-green-500/20' 
                                : 'border-[#BD081C]/50 text-[#BD081C] hover:bg-[#BD081C]/10'}`}
                              data-testid="schedule-pinterest-btn"
                            >
                              {selectedEvent.pinterest_approved ? (
                                <><Check className="w-3.5 h-3.5 mr-1" /> Geplant</>
                              ) : (
                                <><Clock className="w-3.5 h-3.5 mr-1" /> Zum geplanten Zeitpunkt</>
                              )}
                            </Button>
                          </div>
                          {selectedEvent.pinterest_approved && !selectedEvent.pinterest_posted && (
                            <p className="text-[10px] text-green-400 bg-green-500/5 p-2 rounded text-center">
                              Wird automatisch am {new Date(selectedEvent.scheduled_at).toLocaleString('de-DE')} gepostet
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {selectedEvent.pinterest_error && (
                    <p className="text-[10px] text-red-400 mt-1">
                      Fehler: {selectedEvent.pinterest_error}
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowEditModal(true)}
                  variant="outline"
                  className="flex-1 border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                  data-testid="edit-event-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
                <Button
                  onClick={handleDuplicate}
                  variant="outline"
                  className="flex-1 border-[#1A2B42] text-[#A0AEC0] hover:text-white"
                  data-testid="duplicate-event-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplizieren
                </Button>
              </div>
              <button
                onClick={handleDelete}
                className="w-full text-center text-sm text-[#5A6C7D] hover:text-red-400 py-2"
                data-testid="delete-event-btn"
              >
                <Trash className="w-4 h-4 inline mr-1" />
                Löschen
              </button>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* New Post Modal */}
      <Dialog open={showNewPostModal} onOpenChange={setShowNewPostModal}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{t.distribution.newPost}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#A0AEC0] mb-4">
            {t.distribution.addManualContent}
          </p>

          <div className="space-y-4">
            {/* Platform Selector */}
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.platform} *</Label>
              <div className="grid grid-cols-4 gap-2">
                {platforms.map(platform => {
                  const Icon = platform.icon;
                  const isSelected = newPost.platform === platform.key;
                  return (
                    <button
                      key={platform.key}
                      onClick={() => handlePlatformSelect(platform.key)}
                      className={`p-2 flex flex-col items-center gap-1 transition-colors ${
                        isSelected 
                          ? 'bg-[#00D9C8]/10 border border-[#00D9C8]' 
                          : 'bg-[#0A1628] border border-transparent hover:border-[#00D9C8]/50'
                      }`}
                    >
                      <Icon className="w-4 h-4" style={{ color: isSelected ? platform.color : '#5A6C7D' }} />
                      <span className="text-[10px] text-[#A0AEC0]">{platform.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Text / Pinterest Fields */}
            {newPost.platform === 'pinterest' ? (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#A0AEC0] text-xs">{t.produktion.pinTitle} *</Label>
                    <span className={`text-[10px] ${newPost.pin_title.length > 100 ? 'text-red-400' : 'text-[#5A6C7D]'}`}>
                      {newPost.pin_title.length}/100
                    </span>
                  </div>
                  <Input
                    value={newPost.pin_title}
                    onChange={(e) => setNewPost(prev => ({ ...prev, pin_title: e.target.value.slice(0, 100) }))}
                    className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
                    placeholder="Ansprechender Pin-Titel..."
                    maxLength={100}
                    data-testid="pin-title-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#A0AEC0] text-xs">Pin-Beschreibung</Label>
                    <span className={`text-[10px] ${newPost.pin_description.length > 500 ? 'text-red-400' : 'text-[#5A6C7D]'}`}>
                      {newPost.pin_description.length}/500
                    </span>
                  </div>
                  <Textarea
                    value={newPost.pin_description}
                    onChange={(e) => setNewPost(prev => ({ ...prev, pin_description: e.target.value.slice(0, 500) }))}
                    className="min-h-[100px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none"
                    placeholder="Inspirierende Beschreibung..."
                    maxLength={500}
                    data-testid="pin-description-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A0AEC0] text-xs mb-2 block">Link (optional)</Label>
                  <Input
                    value={newPost.pin_link}
                    onChange={(e) => setNewPost(prev => ({ ...prev, pin_link: e.target.value }))}
                    className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
                    placeholder="https://www.beispiel.de/seite"
                    data-testid="pin-link-input"
                  />
                </div>
                {pinterestBoards.length > 0 && (
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">Pinboard *</Label>
                    <select
                      value={newPost.pinterest_board_id}
                      onChange={(e) => setNewPost(prev => ({ ...prev, pinterest_board_id: e.target.value }))}
                      className="w-full rounded-md bg-[#0A1628] border border-transparent focus:border-[#00D9C8] text-white text-sm px-3 py-2"
                      data-testid="pinterest-board-create-select"
                    >
                      {pinterestBoards.map((board) => (
                        <option key={board.id} value={board.id}>{board.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            ) : (
              <div>
                <Label className="text-[#A0AEC0] text-xs mb-2 block">Text *</Label>
                <Textarea
                  value={newPost.text_content}
                  onChange={(e) => setNewPost(prev => ({ ...prev, text_content: e.target.value }))}
                  className="min-h-[120px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none"
                  placeholder="Dein Posting-Text..."
                />
              </div>
            )}

            {/* Images - Multi Upload */}
            {newPost.platform && getMaxImages(newPost.platform) > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#A0AEC0] text-xs">
                  Bilder (max {getMaxImages(newPost.platform)})
                </Label>
                <span className="text-[10px] text-[#5A6C7D]">
                  {newPost.image_urls.length}/{getMaxImages(newPost.platform)}
                </span>
              </div>
              
              {/* Image Grid with numbering and reorder */}
              {newPost.image_urls.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {newPost.image_urls.map((url, idx) => (
                    <div key={idx} className="relative group aspect-square rounded overflow-hidden border border-[#1A2B42]">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {/* Number badge */}
                      <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-[#00D9C8] rounded-full flex items-center justify-center text-[10px] font-bold text-[#0A1628]">
                        {idx + 1}
                      </div>
                      {/* Reorder + Remove controls */}
                      <div className="absolute top-0.5 right-0.5 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {idx > 0 && (
                          <button
                            onClick={() => {
                              setNewPost(prev => {
                                const imgs = [...prev.image_urls];
                                [imgs[idx - 1], imgs[idx]] = [imgs[idx], imgs[idx - 1]];
                                return { ...prev, image_urls: imgs };
                              });
                            }}
                            className="w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-[10px]"
                            data-testid={`move-up-image-${idx}`}
                          >
                            &uarr;
                          </button>
                        )}
                        {idx < newPost.image_urls.length - 1 && (
                          <button
                            onClick={() => {
                              setNewPost(prev => {
                                const imgs = [...prev.image_urls];
                                [imgs[idx], imgs[idx + 1]] = [imgs[idx + 1], imgs[idx]];
                                return { ...prev, image_urls: imgs };
                              });
                            }}
                            className="w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-[10px]"
                            data-testid={`move-down-image-${idx}`}
                          >
                            &darr;
                          </button>
                        )}
                        <button
                          onClick={() => removeImage(idx)}
                          className="w-5 h-5 bg-black/70 rounded-full flex items-center justify-center"
                          data-testid={`remove-image-${idx}`}
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Area */}
              {newPost.image_urls.length < getMaxImages(newPost.platform) && (
                <label 
                  className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded cursor-pointer transition-colors border-[#1A2B42] hover:border-[#00D9C8]/50 bg-[#0A1628]"
                  data-testid="image-upload-area"
                >
                  {isUploadingImage ? (
                    <div className="flex items-center gap-2 text-[#00D9C8]">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Hochladen...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-[#5A6C7D]">
                      <Upload className="w-5 h-5 mb-1" />
                      <span className="text-[10px]">
                        {newPost.image_urls.length > 0 
                          ? `Weitere Bilder hinzufügen (noch ${getMaxImages(newPost.platform) - newPost.image_urls.length})` 
                          : 'Bilder hochladen'}
                      </span>
                    </div>
                  )}
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple
                    className="hidden" 
                    onChange={handleImageUpload}
                    disabled={isUploadingImage}
                    data-testid="image-file-input"
                  />
                </label>
              )}
            </div>
            )}

            {/* Video Upload - only for supported platforms */}
            {newPost.platform && VIDEO_PLATFORMS.includes(newPost.platform) && (
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block">
                {t.distribution.videoOptional}
              </Label>
              
              {newPost.video_url ? (
                <div className="relative rounded overflow-hidden border border-[#1A2B42] bg-[#0A1628]">
                  <video 
                    src={newPost.video_url} 
                    className="w-full max-h-48 object-contain bg-black"
                    controls
                    data-testid="video-preview"
                  />
                  <button
                    onClick={removeVideo}
                    className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center hover:bg-red-500/80 transition-colors"
                    data-testid="remove-video-btn"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <label 
                  className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded cursor-pointer transition-colors border-[#1A2B42] hover:border-[#00D9C8]/50 bg-[#0A1628]"
                  data-testid="video-upload-area"
                >
                  {isUploadingVideo ? (
                    <div className="flex items-center gap-2 text-[#00D9C8]">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">{t.distribution.uploadingVideo}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-[#5A6C7D]">
                      <Film className="w-5 h-5 mb-1" />
                      <span className="text-[10px]">
                        {t.distribution.uploadVideo}
                      </span>
                    </div>
                  )}
                  <input 
                    type="file" 
                    accept="video/mp4,video/quicktime,video/webm,video/x-msvideo,video/mpeg"
                    className="hidden" 
                    onChange={handleVideoUpload}
                    disabled={isUploadingVideo}
                    data-testid="video-file-input"
                  />
                </label>
              )}
            </div>
            )}

            {/* Date/Time */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#A0AEC0] text-xs">Datum & Uhrzeit *</Label>
                {newPost.scheduled_at && newPost.platform && (
                  <span className="text-[10px] text-[#00D9C8]">
                    {t.distribution.bestTimeSuggested}
                  </span>
                )}
              </div>
              <Input
                type="datetime-local"
                value={newPost.scheduled_at}
                onChange={(e) => setNewPost(prev => ({ ...prev, scheduled_at: e.target.value }))}
                className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
                data-testid="new-post-datetime-input"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowNewPostModal(false)}
              className="border-[#1A2B42] text-[#A0AEC0]"
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleCreatePost}
              disabled={isCreating}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
            >
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Posting bearbeiten</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Date/Time */}
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block">Datum & Uhrzeit</Label>
              <Input
                type="datetime-local"
                value={editForm.scheduled_at}
                onChange={(e) => setEditForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
              />
            </div>

            {/* Text */}
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block">Text</Label>
              <Textarea
                value={editForm.text_content}
                onChange={(e) => setEditForm(prev => ({ ...prev, text_content: e.target.value }))}
                className="min-h-[150px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none"
              />
            </div>

            {/* Image URL */}
            {selectedEvent && (
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block">
                Bilder ({editForm.image_urls?.length || 0}/{getMaxImages(selectedEvent.platform)})
              </Label>
              {/* Current images with numbering and reorder */}
              {editForm.image_urls?.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {editForm.image_urls.map((url, idx) => (
                    <div key={idx} className="relative group aspect-square rounded overflow-hidden border border-[#1A2B42]">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {/* Number badge */}
                      <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-[#00D9C8] rounded-full flex items-center justify-center text-[10px] font-bold text-[#0A1628]">
                        {idx + 1}
                      </div>
                      {/* Reorder + Remove controls */}
                      <div className="absolute top-0.5 right-0.5 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {idx > 0 && (
                          <button
                            onClick={() => {
                              setEditForm(prev => {
                                const imgs = [...prev.image_urls];
                                [imgs[idx - 1], imgs[idx]] = [imgs[idx], imgs[idx - 1]];
                                return { ...prev, image_urls: imgs };
                              });
                            }}
                            className="w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-[10px]"
                          >
                            &uarr;
                          </button>
                        )}
                        {idx < editForm.image_urls.length - 1 && (
                          <button
                            onClick={() => {
                              setEditForm(prev => {
                                const imgs = [...prev.image_urls];
                                [imgs[idx], imgs[idx + 1]] = [imgs[idx + 1], imgs[idx]];
                                return { ...prev, image_urls: imgs };
                              });
                            }}
                            className="w-5 h-5 bg-black/70 rounded-full flex items-center justify-center text-white text-[10px]"
                          >
                            &darr;
                          </button>
                        )}
                        <button
                          onClick={() => setEditForm(prev => ({
                            ...prev,
                            image_urls: prev.image_urls.filter((_, i) => i !== idx)
                          }))}
                          className="w-5 h-5 bg-black/70 rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Add more images */}
              {(editForm.image_urls?.length || 0) < getMaxImages(selectedEvent.platform) && (
                <label className="flex flex-col items-center justify-center w-full h-16 border-2 border-dashed rounded cursor-pointer transition-colors border-[#1A2B42] hover:border-[#00D9C8]/50 bg-[#0A1628]">
                  <div className="flex items-center gap-2 text-[#5A6C7D]">
                    <Upload className="w-4 h-4" />
                    <span className="text-xs">Bilder hinzufügen</span>
                  </div>
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple
                    className="hidden"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      const maxAllowed = getMaxImages(selectedEvent.platform);
                      const currentCount = editForm.image_urls?.length || 0;
                      const filesToUpload = files.slice(0, maxAllowed - currentCount);
                      for (const file of filesToUpload) {
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const response = await axios.post(`${API}/upload/image`, formData, {
                            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                          });
                          setEditForm(prev => ({ ...prev, image_urls: [...(prev.image_urls || []), response.data.url] }));
                        } catch (err) {
                          toast.error(`Upload fehlgeschlagen: ${file.name}`);
                        }
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>
            )}

            {/* Video in edit mode - only for supported platforms */}
            {selectedEvent && VIDEO_PLATFORMS.includes(selectedEvent.platform) && (
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block flex items-center gap-1.5">
                <Film className="w-3.5 h-3.5" />
                Video
              </Label>
              {editForm.video_url ? (
                <div className="relative rounded overflow-hidden border border-[#1A2B42] bg-[#0A1628]">
                  <video 
                    src={editForm.video_url} 
                    className="w-full max-h-36 object-contain bg-black"
                    controls
                    data-testid="edit-video-preview"
                  />
                  <button
                    onClick={() => setEditForm(prev => ({ ...prev, video_url: '' }))}
                    className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center hover:bg-red-500/80 transition-colors"
                    data-testid="edit-remove-video-btn"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-16 border-2 border-dashed rounded cursor-pointer transition-colors border-[#1A2B42] hover:border-[#00D9C8]/50 bg-[#0A1628]"
                  data-testid="edit-video-upload-area"
                >
                  <div className="flex items-center gap-2 text-[#5A6C7D]">
                    <Film className="w-4 h-4" />
                    <span className="text-xs">{t.distribution.uploadVideoShort}</span>
                  </div>
                  <input 
                    type="file" 
                    accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 100 * 1024 * 1024) {
                        toast.error(t.distribution.videoTooLarge);
                        return;
                      }
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const response = await axios.post(`${API}/upload/video`, formData, {
                          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                        });
                        setEditForm(prev => ({ ...prev, video_url: response.data.url }));
                        toast.success(t.distribution.videoUploaded);
                      } catch (err) {
                        toast.error(t.distribution.videoUploadFailed);
                      }
                      e.target.value = '';
                    }}
                    data-testid="edit-video-file-input"
                  />
                </label>
              )}
            </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowEditModal(false)}
              className="border-[#1A2B42] text-[#A0AEC0]"
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleEditEvent}
              disabled={isSaving}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LinkedIn Preview Modal */}
      <Dialog open={!!showLinkedinPreview} onOpenChange={(open) => !open && setShowLinkedinPreview(null)}>
        <DialogContent className="bg-[#0F1D32] border-[#1A2B42] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FaLinkedin className="w-5 h-5 text-[#0A66C2]" />
              LinkedIn Vorschau
            </DialogTitle>
          </DialogHeader>

          {showLinkedinPreview && (() => {
            const previewText = getEventText(showLinkedinPreview);
            const previewImages = getEventImages(showLinkedinPreview);
            const charCount = previewText.length;
            const profileName = linkedinProfile.name || 'Dein Name';
            const initials = profileName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
            
            return (
              <div className="space-y-4">
                {/* LinkedIn-style Post Preview */}
                <div className="bg-white rounded-lg overflow-hidden" data-testid="linkedin-preview-card">
                  {/* Post Header */}
                  <div className="p-3 flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-full bg-[#0A66C2] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#000000E6] text-sm font-semibold truncate">{profileName}</p>
                      <p className="text-[#00000099] text-xs truncate">{linkedinProfile.email}</p>
                      <p className="text-[#00000099] text-xs">Jetzt &middot; <span className="inline-block">&#127760;</span></p>
                    </div>
                  </div>

                  {/* Post Text */}
                  <div className="px-3 pb-2">
                    <p className="text-[#000000E6] text-sm whitespace-pre-line leading-relaxed" style={{ wordBreak: 'break-word' }}>
                      {previewText.length > 400 
                        ? previewText.slice(0, 400) + '... mehr anzeigen' 
                        : previewText
                      }
                    </p>
                  </div>

                  {/* Post Images */}
                  {previewImages.length > 0 && (
                    <div className={`${previewImages.length === 1 ? '' : 'grid grid-cols-2 gap-0.5'}`}>
                      {previewImages.slice(0, 4).map((url, idx) => (
                        <div key={idx} className="relative">
                          <img 
                            src={url} 
                            alt="" 
                            className={`w-full object-cover ${
                              previewImages.length === 1 ? 'max-h-72' : 'aspect-square'
                            }`}
                          />
                          {idx === 3 && previewImages.length > 4 && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="text-white text-2xl font-bold">+{previewImages.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Post Actions (LinkedIn style) */}
                  <div className="px-3 py-2 border-t border-gray-200 flex justify-around">
                    {['Gefällt mir', 'Kommentieren', 'Reposten', 'Senden'].map((action) => (
                      <span key={action} className="text-[#00000099] text-xs font-medium">{action}</span>
                    ))}
                  </div>
                </div>

                {/* Stats Bar */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <span className={`${charCount > 3000 ? 'text-red-400' : 'text-[#5A6C7D]'}`}>
                      {charCount.toLocaleString()} Zeichen
                      {charCount > 3000 && ' (LinkedIn empfiehlt max. 3.000)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {previewImages.length > 0 && (
                      <span className="text-[#5A6C7D]">
                        {previewImages.length} Bild{previewImages.length > 1 ? 'er' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Warning if no images */}
                {previewImages.length === 0 && (
                  <div className="bg-[#1A2B42] rounded p-2.5 text-xs text-[#A0AEC0] flex items-center gap-2">
                    <span>Posts mit Bildern erhalten bis zu 2x mehr Engagement auf LinkedIn</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowLinkedinPreview(null)}
                    className="flex-1 border-[#1A2B42] text-[#A0AEC0] hover:text-white"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={async () => {
                      const eventId = showLinkedinPreview.id;
                      setShowLinkedinPreview(null);
                      await handlePublishLinkedin(eventId);
                    }}
                    disabled={publishingLinkedin === showLinkedinPreview?.id}
                    className="flex-1 bg-[#0A66C2] hover:bg-[#0A66C2]/80 text-white font-medium"
                    data-testid="confirm-linkedin-publish-btn"
                  >
                    {publishingLinkedin === showLinkedinPreview?.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <FaLinkedin className="w-4 h-4 mr-2" />
                    )}
                    Jetzt veröffentlichen
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Pinterest Preview Modal */}
      <Dialog open={!!showPinterestPreview} onOpenChange={(open) => !open && setShowPinterestPreview(null)}>
        <DialogContent className="bg-[#0F1D32] border-[#1A2B42] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FaPinterest className="w-5 h-5 text-[#BD081C]" />
              Pinterest Vorschau
            </DialogTitle>
          </DialogHeader>

          {showPinterestPreview && (() => {
            const previewImages = getEventImages(showPinterestPreview);
            const imageUrl = previewImages[0] || '';
            
            const pinTitle = getPinTitle(showPinterestPreview);
            const pinDesc = getPinDescription(showPinterestPreview);
            
            return (
              <div className="space-y-4">
                {/* Pinterest-style Pin Preview */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg max-w-[260px] mx-auto" data-testid="pinterest-preview-card">
                  {/* Pin Image */}
                  {imageUrl && (
                    <img 
                      src={imageUrl} 
                      alt="" 
                      className="w-full object-cover"
                      style={{ maxHeight: '320px' }}
                    />
                  )}
                  {/* Pin Info */}
                  <div className="p-3">
                    <h3 className="text-[#111] text-sm font-semibold leading-tight mb-1">{pinTitle}</h3>
                    <p className="text-[#767676] text-xs line-clamp-2">{pinDesc.slice(0, 150)}</p>
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="w-6 h-6 rounded-full bg-[#BD081C] flex items-center justify-center">
                        <span className="text-white text-[8px] font-bold">
                          {(pinterestProfile.name || 'P')[0].toUpperCase()}
                        </span>
                      </div>
                      <span className="text-[#111] text-xs font-medium">
                        {pinterestProfile.name || pinterestProfile.username || 'Dein Profil'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-[#5A6C7D]">
                  <span>Titel: {pinTitle.length}/100 Zeichen</span>
                  <span>Beschreibung: {Math.min(pinDesc.length, 500)}/500</span>
                </div>

                {/* Board Selection */}
                <div>
                  <label className="text-xs text-[#A0AEC0] mb-1 block">Board auswählen</label>
                  <select
                    value={selectedBoardId}
                    onChange={(e) => setSelectedBoardId(e.target.value)}
                    className="w-full bg-[#0A1628] border border-[#1A2B42] rounded px-3 py-2 text-sm text-white focus:border-[#BD081C] outline-none"
                    data-testid="pinterest-board-select"
                  >
                    {pinterestBoards.length === 0 && (
                      <option value="">Keine Boards verfügbar</option>
                    )}
                    {pinterestBoards.map((board) => (
                      <option key={board.id} value={board.id}>{board.name}</option>
                    ))}
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowPinterestPreview(null)}
                    className="flex-1 border-[#1A2B42] text-[#A0AEC0] hover:text-white"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={() => handlePublishPinterest(showPinterestPreview.id, selectedBoardId)}
                    disabled={publishingPinterest === showPinterestPreview?.id || !selectedBoardId}
                    className="flex-1 bg-[#BD081C] hover:bg-[#BD081C]/80 text-white font-medium"
                    data-testid="confirm-pinterest-publish-btn"
                  >
                    {publishingPinterest === showPinterestPreview?.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <FaPinterest className="w-4 h-4 mr-2" />
                    )}
                    Jetzt pinnen
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Duplicate Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {t.distribution.duplicatePost}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#A0AEC0] mb-4">
            {lang === 'de'
              ? 'Der Inhalt wird kopiert. Wähle Plattform, Datum und Uhrzeit für das Duplikat.'
              : 'Content will be copied. Choose platform, date and time for the duplicate.'}
          </p>
          <div className="space-y-4">
            {/* Platform */}
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block">
                {t.distribution.platformLabel}
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {platforms.map(p => {
                  const Icon = p.icon;
                  const isSelected = duplicateForm.platform === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setDuplicateForm(prev => ({ ...prev, platform: p.key }))}
                      className={`p-2 flex flex-col items-center gap-1 transition-colors ${
                        isSelected
                          ? 'bg-[#00D9C8]/10 border border-[#00D9C8]'
                          : 'bg-[#0A1628] border border-transparent hover:border-[#00D9C8]/50'
                      }`}
                      data-testid={`dup-platform-${p.key}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: isSelected ? p.color : '#5A6C7D' }} />
                      <span className="text-[10px] text-[#A0AEC0]">{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Date/Time */}
            <div>
              <Label className="text-[#A0AEC0] text-xs mb-2 block">
                {t.distribution.dateTime}
              </Label>
              <Input
                type="datetime-local"
                value={duplicateForm.scheduled_at}
                onChange={(e) => setDuplicateForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
                data-testid="duplicate-datetime-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDuplicateModal(false)}
              className="border-[#1A2B42] text-[#A0AEC0]"
            >
              {t.common.cancel}
            </Button>
            <Button
              onClick={handleDuplicateConfirm}
              disabled={isDuplicating || !duplicateForm.scheduled_at}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
              data-testid="confirm-duplicate-btn"
            >
              {isDuplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  {t.common.duplicate}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShowcaseDistribution;

